import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Space,
  message,
  Spin,
  Row,
  Col,
  Table,
  InputNumber,
  Modal,
  Popconfirm,
  Typography,
  Divider,
} from "antd";
import {
  ArrowLeftOutlined,
  SaveOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageHeader from "../../components/common/PageHeader";
import ImageUpload from "../../components/common/ImageUpload";
import {
  SectionType,
  CreateSectionTypeCommand,
  UpdateSectionTypeCommand,
  SectionDetail,
} from "../../types/sectionType";
import { FixtureType } from "../../types/fixture";
import sectionTypeService from "../../services/sectionTypeService";
import fixtureService from "../../services/fixtureService";
import Upload from "../../components/common/Upload";
import "./sectionType.css";

const { TextArea } = Input;
const { Title } = Typography;

interface SectionTypeFormPageProps {
  // 可以添加额外的props
}

const SectionTypeFormPage: React.FC<SectionTypeFormPageProps> = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [detailForm] = Form.useForm();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sectionType, setSectionType] = useState<SectionType | null>(null);

  // Section Details 相关状态
  const [sectionDetails, setSectionDetails] = useState<SectionDetail[]>([]);
  const [editingDetail, setEditingDetail] = useState<SectionDetail | null>(
    null
  );
  const [detailModalVisible, setDetailModalVisible] = useState(false);

  // Fixture Type 选择相关状态
  const [fixtureModalVisible, setFixtureModalVisible] = useState(false);
  const [fixtureTypes, setFixtureTypes] = useState<FixtureType[]>([]);
  const [fixtureLoading, setFixtureLoading] = useState(false);
  const [selectedFixture, setSelectedFixture] = useState<FixtureType | null>(
    null
  );
  const [currentEditingIndex, setCurrentEditingIndex] = useState<number>(-1);

  const isEdit = Boolean(id);
  const pageTitle = isEdit
    ? t("sectionType.editTitle")
    : t("sectionType.createTitle");

  // 获取货架类型详情（编辑模式）
  const fetchSectionTypeDetail = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const response = await sectionTypeService.getSectionTypeById(Number(id));
      if (response.success && response.data) {
        const data = response.data;
        setSectionType(data);

        // 检查是否可以编辑
        if (data.status !== "DRAFT") {
          message.error(t("sectionType.onlyDraftCanEdit"));
          navigate("/section-type");
          return;
        }

        // 填充表单
        form.setFieldsValue({
          code: data.typeCode,
          name: data.typeName,
          description: data.description,
          innerLength: data.innerLength || 0,
          innerWidth: data.innerWidth || 0,
          innerHeight: data.innerHeight || 0,
          outerLength: data.outerLength || 0,
          outerWidth: data.outerWidth || 0,
          outerHeight: data.outerHeight || 0,
          lengthUnit: data.lengthUnit || "mm",
          imageUrl: data.imageUrl || "",
        });

        // 填充货架明细
        if (data.sectionDetails && data.sectionDetails.length > 0) {
          setSectionDetails(data.sectionDetails);
        }
      } else {
        message.error(
          response.errorMessage || t("sectionType.getDetailFailed")
        );
        navigate("/section-type");
      }
    } catch (error) {
      console.error("获取货架类型详情失败:", error);
      message.error(t("sectionType.getDetailFailed"));
      navigate("/section-type");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isEdit) {
      fetchSectionTypeDetail();
    }
  }, [id, isEdit]);

  // 返回列表
  const handleBack = () => {
    navigate("/section-type");
  };

  // 表单提交
  const handleSubmit = async (values: any) => {
    setSubmitting(true);
    try {
      let response;

      if (isEdit && sectionType) {
        // 编辑模式
        const command: UpdateSectionTypeCommand = {
          id: sectionType.id,
          typeCode: values.code,
          typeName: values.name,
          description: values.description,
          innerLength: values.innerLength,
          innerWidth: values.innerWidth,
          innerHeight: values.innerHeight,
          outerLength: values.outerLength,
          outerWidth: values.outerWidth,
          outerHeight: values.outerHeight,
          lengthUnit: values.lengthUnit,
          imageUrl: values.imageUrl?.[0],
          sectionDetails: sectionDetails,
        };
        response = await sectionTypeService.updateSectionType(command);
      } else {
        // 创建模式
        const command: CreateSectionTypeCommand = {
          typeCode: values.code,
          typeName: values.name,
          description: values.description,
          innerLength: values.innerLength,
          innerWidth: values.innerWidth,
          innerHeight: values.innerHeight,
          outerLength: values.outerLength,
          outerWidth: values.outerWidth,
          outerHeight: values.outerHeight,
          lengthUnit: values.lengthUnit,
          imageUrl: values.imageUrl?.[0],
          sectionDetails: sectionDetails,
        };
        response = await sectionTypeService.createSectionType(command);
      }

      if (response.success) {
        const successMessage = isEdit
          ? t("sectionType.updateSuccess")
          : t("sectionType.createSuccess");
        message.success(successMessage);
        navigate("/section-type");
      } else {
        message.error(
          response.errorMessage || t("sectionType.operationFailed")
        );
      }
    } catch (error) {
      console.error("表单提交失败:", error);
      message.error(t("sectionType.operationFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  // 表单验证失败
  const handleSubmitFailed = (errorInfo: any) => {
    console.log("表单验证失败:", errorInfo);
    message.error(t("sectionType.formValidationFailed"));
  };

  // 获取FixtureType列表
  const fetchFixtureTypes = async () => {
    setFixtureLoading(true);
    try {
      const response = await fixtureService.getFixtures(
        { status: "Active" },
        { currentPage: 1, pageSize: 100 }
      );
      if (response.success && response.data) {
        setFixtureTypes(response.data.tableData);
      } else {
        message.error(
          response.errorMessage
            ? t("sectionType.getFixtureTypesFailed")
            : t("sectionType.getFixtureTypesFailed")
        );
      }
    } catch (error) {
      console.error("获取夹具类型列表失败:", error);
      message.error(t("sectionType.getFixtureTypesFailed"));
    } finally {
      setFixtureLoading(false);
    }
  };

  // 添加新的货架明细
  const handleAddDetail = () => {
    setDetailModalVisible(true);
    setCurrentEditingIndex(-1);
  };

  // 编辑货架明细
  const handleEditDetail = (index: number) => {
    setEditingDetail({ ...sectionDetails[index] });
    setCurrentEditingIndex(index);
    setDetailModalVisible(true);
  };

  // 删除货架明细
  const handleDeleteDetail = (index: number) => {
    const newDetails = sectionDetails.filter(
      (_: SectionDetail, i: number) => i !== index
    );
    // 不重新排序layerIndex，保持原有的索引值
    setSectionDetails(newDetails);
  };

  // 保存货架明细
  const handleSaveDetail = (detail: SectionDetail) => {
    if (currentEditingIndex >= 0) {
      // 编辑模式
      const newDetails = [...sectionDetails];
      newDetails[currentEditingIndex] = detail;
      setSectionDetails(newDetails);
    } else {
      // 新增模式
      setSectionDetails([...sectionDetails, detail]);
    }
    setDetailModalVisible(false);
    setEditingDetail(null); // 确保清空editingDetail
    setCurrentEditingIndex(-1);
  };

  // 打开FixtureType选择弹框
  const handleSelectFixtureType = () => {
    fetchFixtureTypes();
    setFixtureModalVisible(true);
  };

  // 确认选择FixtureType
  const handleConfirmFixtureSelection = () => {
    if (selectedFixture && editingDetail) {
      const updatedDetail = {
        ...editingDetail,
        fixtureTypeId: selectedFixture.id,
        fixtureTypeName: selectedFixture.typeName,
        fixtureTypeCode: selectedFixture.typeCode,
        // 从夹具类型获取尺寸数据
        layerHeight: selectedFixture.dimensions.height,
        layerDepth: selectedFixture.dimensions.depth,
        layerWidth: selectedFixture.dimensions.width,
      };

      setEditingDetail(updatedDetail);
      // 同时更新表单字段的值，确保表单校验能正确识别
      detailForm.setFieldsValue({
        fixtureTypeId: selectedFixture.id,
      });
    }

    setFixtureModalVisible(false);
    setSelectedFixture(null);
  };

  // 取消选择FixtureType
  const handleCancelFixtureSelection = () => {
    setFixtureModalVisible(false);
    setSelectedFixture(null);
  };

  if (loading) {
    return (
      <div className="section-type-form">
        <div className="section-type-loading">
          <Spin size="large" tip={t("common.loading")} />
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={pageTitle}
        backPath="/section-type"
        extra={
          <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
            {t("common.back")}
          </Button>
        }
      />

      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onFinishFailed={handleSubmitFailed}
          disabled={submitting}
          // 阻止默认提交
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
            }
          }}
        >
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label={t("sectionType.code")}
                name="code"
                rules={[
                  { required: true, message: t("sectionType.codeRequired") },
                  { max: 50, message: t("sectionType.codeMaxLength") },
                  {
                    pattern: /^[A-Za-z0-9_-]+$/,
                    message: t("sectionType.codePattern"),
                  },
                ]}
              >
                <Input
                  placeholder={t("sectionType.codePlaceholder")}
                  maxLength={50}
                  disabled={isEdit}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={t("sectionType.name")}
                name="name"
                rules={[
                  { required: true, message: t("sectionType.nameRequired") },
                  { max: 100, message: t("sectionType.nameMaxLength") },
                ]}
              >
                <Input
                  placeholder={t("sectionType.namePlaceholder")}
                  maxLength={100}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={24}>
              <Form.Item
                label={t("sectionType.description")}
                name="description"
                rules={[
                  { max: 500, message: t("sectionType.descriptionMaxLength") },
                ]}
              >
                <TextArea
                  placeholder={t("sectionType.descriptionPlaceholder")}
                  rows={4}
                  maxLength={500}
                  showCount
                />
              </Form.Item>
            </Col>
          </Row>

          {/* 货架类型尺寸信息 */}
          <Row gutter={24}>
            <Col span={8}>
              <Form.Item
                label={t("sectionType.innerLength")}
                name="innerLength"
                rules={[
                  {
                    required: true,
                    message: t("sectionType.innerLengthRequired"),
                  },
                ]}
              >
                <InputNumber
                  min={0.1}
                  precision={1}
                  style={{ width: "100%" }}
                  placeholder={t("sectionType.innerLengthPlaceholder")}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label={t("sectionType.innerWidth")}
                name="innerWidth"
                rules={[
                  {
                    required: true,
                    message: t("sectionType.innerWidthRequired"),
                  },
                ]}
              >
                <InputNumber
                  min={0.1}
                  precision={1}
                  style={{ width: "100%" }}
                  placeholder={t("sectionType.innerWidthPlaceholder")}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label={t("sectionType.innerHeight")}
                name="innerHeight"
                rules={[
                  {
                    required: true,
                    message: t("sectionType.innerHeightRequired"),
                  },
                ]}
              >
                <InputNumber
                  min={0.1}
                  precision={1}
                  style={{ width: "100%" }}
                  placeholder={t("sectionType.innerHeightPlaceholder")}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={8}>
              <Form.Item
                label={t("sectionType.outerLength")}
                name="outerLength"
                rules={[
                  {
                    required: true,
                    message: t("sectionType.outerLengthRequired"),
                  },
                ]}
              >
                <InputNumber
                  min={0.1}
                  precision={1}
                  style={{ width: "100%" }}
                  placeholder={t("sectionType.outerLengthPlaceholder")}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label={t("sectionType.outerWidth")}
                name="outerWidth"
                rules={[
                  {
                    required: true,
                    message: t("sectionType.outerWidthRequired"),
                  },
                ]}
              >
                <InputNumber
                  min={0.1}
                  precision={1}
                  style={{ width: "100%" }}
                  placeholder={t("sectionType.outerWidthPlaceholder")}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label={t("sectionType.outerHeight")}
                name="outerHeight"
                rules={[
                  {
                    required: true,
                    message: t("sectionType.outerHeightRequired"),
                  },
                ]}
              >
                <InputNumber
                  min={0.1}
                  precision={1}
                  style={{ width: "100%" }}
                  placeholder={t("sectionType.outerHeightPlaceholder")}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label={t("sectionType.image")}
                name="imageUrl"
                // rules={[
                //   {
                //     required: true,
                //     message: t("sectionType.imageRequired"),
                //   },
                // ]}
              >
                <Upload
                  maxCount={1}
                  placeholder={t("sectionType.imagePlaceholder")}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          {/* 货架明细部分 */}
          <Row gutter={24}>
            <Col span={24}>
              <div style={{ marginBottom: 16 }}>
                <Title level={4}>{t("sectionType.sectionDetails")}</Title>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddDetail}
                  style={{ marginBottom: 16 }}
                >
                  {t("sectionType.addDetail")}
                </Button>
              </div>

              <Table
                dataSource={sectionDetails}
                rowKey={(record, index) => index?.toString() || "0"}
                pagination={false}
                size="small"
                scroll={{ y: 500 }}
                columns={[
                  {
                    title: t("sectionType.layer"),
                    dataIndex: "layerIndex",
                    key: "layerIndex",
                    width: 80,
                  },
                  {
                    title: t("sectionType.fixtureType"),
                    key: "fixtureType",
                    width: 100,
                    render: (record: SectionDetail) => {
                      // console.log("****", record);

                      return (
                        <div>
                          <div>
                            {record.fixtureTypeName ||
                              t("sectionType.notSelected")}
                          </div>
                          <div style={{ fontSize: "12px", color: "#666" }}>
                            {record.fixtureTypeCode || ""}
                          </div>
                        </div>
                      );
                    },
                  },
                  {
                    title: t("sectionType.quantity"),
                    dataIndex: "quantity",
                    key: "quantity",
                    width: 100,
                    render: (value: number) => value || 1,
                  },
                  {
                    title: t("sectionType.holePosition"),
                    dataIndex: "holePosition",
                    key: "holePosition",
                    width: 100,
                  },

                  {
                    title: t("common.actions"),
                    key: "actions",
                    width: 120,
                    render: (_: any, record: SectionDetail, index: number) => (
                      <Space>
                        <Button
                          type="link"
                          size="small"
                          icon={<EditOutlined />}
                          onClick={() => handleEditDetail(index)}
                        >
                          {t("common.edit")}
                        </Button>
                        <Popconfirm
                          title={t("sectionType.confirmDeleteDetail")}
                          onConfirm={() => handleDeleteDetail(index)}
                          okText={t("common.confirm")}
                          cancelText={t("common.cancel")}
                        >
                          <Button
                            type="link"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                          >
                            {t("common.delete")}
                          </Button>
                        </Popconfirm>
                      </Space>
                    ),
                  },
                ]}
              />
            </Col>
          </Row>

          <div className="form-actions">
            <Space size="large">
              <Button size="large" onClick={handleBack} disabled={submitting}>
                {t("common.cancel")}
              </Button>
              <Button
                type="primary"
                size="large"
                htmlType="submit"
                // htmlType="button"
                icon={<SaveOutlined />}
                loading={submitting}
              >
                {isEdit ? t("common.save") : t("common.create")}
              </Button>
            </Space>
          </div>
        </Form>
      </Card>

      {/* 货架明细编辑弹框 */}
      <Modal
        title={
          currentEditingIndex >= 0
            ? t("sectionType.editDetail")
            : t("sectionType.addDetail")
        }
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setEditingDetail(null); // 清空editingDetail
          setCurrentEditingIndex(-1);
          // 重置表单和校验状态
          detailForm.resetFields();
        }}
        footer={null}
        width={600}
        afterOpenChange={(open) => {
          // 当模态框打开时，如果是新增模式，则计算新的layerIndex
          if (open && currentEditingIndex < 0) {
            // 计算新的layerIndex为当前最大的layerIndex + 1，如果没有则默认为1
            const maxLayerIndex =
              sectionDetails.length > 0
                ? Math.max(...sectionDetails.map((detail) => detail.layerIndex))
                : 0;
            const newLayerIndex = maxLayerIndex + 1;

            const newDetail: SectionDetail = {
              layerIndex: newLayerIndex,
              layerHeight: 0,
              layerDepth: 0,
              layerWidth: 0,
              quantity: 1, // 添加默认数量
              holePosition: "", // 添加默认孔位
            };

            setEditingDetail(newDetail);
            // 设置表单字段的值，但不重置校验状态
            detailForm.setFieldsValue({
              ...newDetail,
              fixtureTypeId: newDetail.fixtureTypeId || undefined,
            });
          } else if (open && currentEditingIndex >= 0 && editingDetail) {
            // 编辑模式下，设置表单字段的值，但不重置校验状态
            detailForm.setFieldsValue({
              ...editingDetail,
              fixtureTypeId: editingDetail.fixtureTypeId || undefined,
            });
          }
        }}
      >
        {editingDetail && (
          <Form
            form={detailForm}
            layout="vertical"
            onFinish={(values) => {
              // 对于新增模式，始终使用计算出的layerIndex
              // 对于编辑模式，使用表单中的layerIndex
              const detailToSave = {
                ...editingDetail,
                ...values,
                // 确保使用editingDetail中的fixtureType信息，因为只读输入框不会包含在values中
                fixtureTypeId: editingDetail?.fixtureTypeId,
                fixtureTypeName: editingDetail?.fixtureTypeName,
                fixtureTypeCode: editingDetail?.fixtureTypeCode,
              };
              handleSaveDetail(detailToSave);
            }}
            onFinishFailed={(errorInfo) => {
              console.log("表单校验失败:", errorInfo);
            }}
            key={currentEditingIndex} // 添加key确保表单重新渲染
          >
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label={t("sectionType.layerIndex")}
                  name="layerIndex"
                  rules={[
                    {
                      required: true,
                      message: t("sectionType.layerIndexRequired"),
                    },
                  ]}
                >
                  <InputNumber
                    min={1}
                    style={{ width: "100%" }}
                    disabled
                    placeholder={t("sectionType.layerIndexPlaceholder")}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label={t("sectionType.quantity")}
                  name="quantity"
                  rules={[
                    {
                      required: true,
                      message: t("sectionType.quantityRequired"),
                    },
                  ]}
                >
                  <InputNumber
                    min={1}
                    style={{ width: "100%" }}
                    placeholder={t("sectionType.quantityPlaceholder")}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label={t("sectionType.holePosition")}
                  name="holePosition"
                >
                  <Input
                    placeholder={t("sectionType.holePositionPlaceholder")}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item label={t("sectionType.fixtureType")} required>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <Input
                      value={
                        editingDetail.fixtureTypeName ||
                        t("sectionType.notSelected")
                      }
                      readOnly
                      placeholder={t("sectionType.selectFixtureType")}
                      style={{ flex: 1 }}
                    />
                    <Button onClick={handleSelectFixtureType}>
                      {t("sectionType.selectFixtureType")}
                    </Button>
                  </div>
                  {editingDetail.fixtureTypeCode && (
                    <div
                      style={{ fontSize: "12px", color: "#666", marginTop: 4 }}
                    >
                      {t("sectionType.code")}: {editingDetail.fixtureTypeCode}
                    </div>
                  )}
                  {/* 隐藏字段用于存储选中的fixtureTypeId，确保表单校验正常工作 */}
                  <Form.Item
                    name="fixtureTypeId"
                    noStyle
                    rules={[
                      {
                        required: true,
                        message: t("sectionType.fixtureTypeRequired"),
                      },
                    ]}
                  >
                    <Input type="hidden" />
                  </Form.Item>
                </Form.Item>
              </Col>
            </Row>

            <div style={{ textAlign: "right", marginTop: 16 }}>
              <Space>
                <Button
                  onClick={() => {
                    setDetailModalVisible(false);
                    setEditingDetail(null);
                    setCurrentEditingIndex(-1);
                    // 重置表单和校验状态
                    detailForm.resetFields();
                  }}
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  type="primary"
                  onClick={() => {
                    // 触发表单校验和提交
                    detailForm.submit();
                  }}
                >
                  {t("common.confirm")}
                </Button>
              </Space>
            </div>
          </Form>
        )}
      </Modal>

      {/* 夹具类型选择弹框 */}
      <Modal
        title={t("sectionType.selectFixtureType")}
        open={fixtureModalVisible}
        onCancel={handleCancelFixtureSelection}
        footer={[
          <Button key="cancel" onClick={handleCancelFixtureSelection}>
            {t("common.cancel")}
          </Button>,
          <Button
            key="confirm"
            type="primary"
            onClick={handleConfirmFixtureSelection}
            disabled={!selectedFixture}
          >
            {t("sectionType.confirmSelection")}
          </Button>,
        ]}
        width={800}
      >
        <Table
          dataSource={fixtureTypes}
          rowKey="id"
          loading={fixtureLoading}
          pagination={false}
          size="small"
          scroll={{ x: 1200, y: 500 }}
          rowSelection={{
            type: "radio",
            selectedRowKeys: selectedFixture ? [selectedFixture.id] : [],
            onChange: (selectedRowKeys: React.Key[]) => {
              if (selectedRowKeys.length > 0) {
                const selected = fixtureTypes.find(
                  (f) => f.id === selectedRowKeys[0]
                );
                setSelectedFixture(selected || null);
              } else {
                setSelectedFixture(null);
              }
            },
          }}
          columns={[
            {
              title: t("sectionType.fixtureTypeName"),
              dataIndex: "typeName",
              key: "typeName",
            },
            {
              title: t("sectionType.fixtureTypeCode"),
              dataIndex: "typeCode",
              key: "typeCode",
            },
            {
              title: t("fixture.dimensions"),
              key: "dimensions",
              render: (record: FixtureType) => (
                <span>
                  {record.dimensions.width} × {record.dimensions.height} ×{" "}
                  {record.dimensions.depth} {record.dimensions.unit}
                </span>
              ),
            },
            {
              title: t("fixture.holeCount"),
              dataIndex: "holeCount",
              key: "holeCount",
            },
            {
              title: t("common.status"),
              dataIndex: "status",
              key: "status",
              render: (status: string) => (
                <span
                  style={{ color: status === "Active" ? "#52c41a" : "#999" }}
                >
                  {status === "Active"
                    ? t("fixture.statusText.Active")
                    : status === "Draft"
                    ? t("fixture.statusText.Draft")
                    : t("fixture.statusText.Inactive")}
                </span>
              ),
            },
          ]}
        />

        {selectedFixture && (
          <div
            style={{
              marginTop: 16,
              padding: 16,
              backgroundColor: "#f5f5f5",
              borderRadius: 4,
            }}
          >
            <Title level={5}>{t("sectionType.fixtureTypeDetails")}</Title>
            <Row gutter={16}>
              <Col span={12}>
                <p>
                  <strong>{t("fixture.name")}:</strong>{" "}
                  {selectedFixture.typeName}
                </p>
                <p>
                  <strong>{t("fixture.code")}:</strong>{" "}
                  {selectedFixture.typeCode}
                </p>
                <p>
                  <strong>{t("fixture.status")}:</strong>{" "}
                  {selectedFixture.status === "Active"
                    ? t("fixture.statusText.Active")
                    : status === "Draft"
                    ? t("fixture.statusText.Draft")
                    : t("fixture.statusText.Inactive")}
                </p>
              </Col>
              <Col span={12}>
                <p>
                  <strong>{t("fixture.dimensions")}:</strong>{" "}
                  {selectedFixture.dimensions.width} ×{" "}
                  {selectedFixture.dimensions.height} ×{" "}
                  {selectedFixture.dimensions.depth}{" "}
                  {selectedFixture.dimensions.unit}
                </p>
                <p>
                  <strong>{t("fixture.holeCount")}:</strong>{" "}
                  {selectedFixture.holeCount}
                </p>
                {selectedFixture.description && (
                  <p>
                    <strong>{t("fixture.description")}:</strong>{" "}
                    {selectedFixture.description}
                  </p>
                )}
              </Col>
            </Row>
            {selectedFixture.imageUrl && (
              <div style={{ marginTop: 16 }}>
                <strong>{t("sectionType.fixtureTypeImage")}:</strong>
                <div style={{ marginTop: 8 }}>
                  <img
                    src={selectedFixture.imageUrl}
                    alt={""}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "200px",
                      objectFit: "contain",
                      border: "1px solid #ddd",
                      borderRadius: 4,
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SectionTypeFormPage;
